import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

export default function OrderLivePaymentStatus() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);

  const fetchStatus = async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setRefreshing(true);
    else setLoading(true);
    setError('');
    try {
      const res = await axios.get(`https://sarvpriya.onrender.com/public/order-payment-status/${orderId}`);
      setOrder(res.data);
    } catch (err) {
      console.error(err);
      setError('Order details could not be retrieved. Please check the URL link or try again.');
    }
    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    fetchStatus();
  }, [orderId]);

  if (loading) {
    return (
      <div style={styles.fullscreenContainer}>
        <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ marginTop: '16px', color: '#1a237e', fontWeight: '600' }}>Fetching Order Status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.fullscreenContainer}>
        <div style={styles.card}>
          <div style={{ fontSize: '50px', color: '#ed3237', marginBottom: '16px' }}>⚠️</div>
          <h4 style={{ color: '#ed3237', fontWeight: 'bold' }}>Error</h4>
          <p style={{ color: '#666', fontSize: '14px', marginVertical: '12px' }}>{error}</p>
          <button className="btn btn-outline-danger btn-sm" onClick={() => fetchStatus()}>Retry</button>
        </div>
      </div>
    );
  }

  const isPaid = order?.paymentStatus?.toLowerCase() === 'paid';
  const isCod = order?.paymentMethod?.toLowerCase() === 'cod';

  return (
    <div style={styles.pageBackground}>
      <div style={styles.wrapper}>
        {/* Header */}
        <div style={styles.logoHeader}>
          <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 'bold', color: '#ffd600' }}>BEST BASKET</h3>
          <p style={{ margin: 0, fontSize: '11px', color: '#ffffff', opacity: 0.85 }}>Secure Payment Tracker</p>
        </div>

        {/* Card Body */}
        <div style={styles.mainCard}>
          <p style={{ color: '#888', fontSize: '13px', margin: 0 }}>Order Number</p>
          <h2 style={{ color: '#1a237e', fontWeight: 'bold', marginTop: '4px', marginBottom: '20px' }}>{order?.orderNumber}</h2>

          {/* Status Indicators */}
          {isPaid ? (
            <div style={{ ...styles.statusCard, ...styles.statusCardPaid }}>
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>🟢</div>
              <h4 style={{ color: '#1b5e20', fontWeight: 'bold', fontSize: '18px', margin: 0 }}>PAID ONLINE</h4>
              <p style={{ color: '#2e7d32', fontSize: '13px', fontWeight: 'bold', marginTop: '6px', marginBottom: 0 }}>DO NOT COLLECT CASH</p>
            </div>
          ) : (
            <div style={{ ...styles.statusCard, ...styles.statusCardPending }}>
              <div style={{ fontSize: '36px', marginBottom: '8px' }}>🔴</div>
              <h4 style={{ color: '#b71c1c', fontWeight: 'bold', fontSize: '18px', margin: 0 }}>PENDING CASH</h4>
              {isCod ? (
                <p style={{ color: '#c62828', fontSize: '13px', fontWeight: 'bold', marginTop: '6px', marginBottom: 0 }}>
                  COLLECT ₹{order?.totalAmount} CASH
                </p>
              ) : (
                <p style={{ color: '#c62828', fontSize: '13px', fontWeight: 'bold', marginTop: '6px', marginBottom: 0 }}>
                  Payment failed. Collect ₹{order?.totalAmount} Cash
                </p>
              )}
            </div>
          )}

          {/* Detail Grid */}
          <div style={styles.gridContainer}>
            <div style={styles.gridRow}>
              <span style={styles.gridLabel}>Total Amount:</span>
              <span style={styles.gridValue}>₹{order?.totalAmount}</span>
            </div>
            <div style={styles.gridRow}>
              <span style={styles.gridLabel}>Original Method:</span>
              <span style={styles.gridValue} style={{ textTransform: 'uppercase' }}>{order?.paymentMethod}</span>
            </div>
            <div style={styles.gridRow}>
              <span style={styles.gridLabel}>Order Status:</span>
              <span style={styles.gridValue} style={{ textTransform: 'uppercase', color: '#1565c0', fontWeight: 'bold' }}>{order?.status}</span>
            </div>
          </div>

          {/* Refresh Action */}
          <button 
            className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
            style={styles.refreshButton}
            onClick={() => fetchStatus(true)}
            disabled={refreshing}
          >
            {refreshing ? (
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
            ) : null}
            {refreshing ? 'Refreshing...' : 'Refresh Payment Status'}
          </button>

          <p style={styles.footerNote}>
            Ask the customer to refresh their app if they have just made the payment.
          </p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  fullscreenContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f6fa',
    padding: '24px',
    textAlign: 'center'
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    maxWidth: '360px',
    width: '100%'
  },
  pageBackground: {
    backgroundColor: '#f4f6fa',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '20px 12px'
  },
  wrapper: {
    maxWidth: '420px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch'
  },
  logoHeader: {
    backgroundColor: '#1a237e',
    borderRadius: '12px 12px 0 0',
    padding: '16px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
  },
  mainCard: {
    backgroundColor: '#ffffff',
    borderRadius: '0 0 12px 12px',
    padding: '24px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
    textAlign: 'center'
  },
  statusCard: {
    borderRadius: '12px',
    padding: '20px 16px',
    marginBottom: '24px',
    borderWidth: '1px',
    borderStyle: 'solid'
  },
  statusCardPaid: {
    backgroundColor: '#e8f5e9',
    borderColor: '#a5d6a7'
  },
  statusCardPending: {
    backgroundColor: '#ffebee',
    borderColor: '#ffcdd2'
  },
  gridContainer: {
    borderTop: '1px solid #eee',
    borderBottom: '1px solid #eee',
    paddingVertical: '12px',
    marginBottom: '24px',
    textAlign: 'left'
  },
  gridRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 0',
    fontSize: '14px'
  },
  gridLabel: {
    color: '#666'
  },
  gridValue: {
    fontWeight: 'bold',
    color: '#333'
  },
  refreshButton: {
    backgroundColor: '#1a237e',
    borderColor: '#1a237e',
    paddingVertical: '12px',
    fontSize: '15px',
    fontWeight: 'bold',
    borderRadius: '8px'
  },
  footerNote: {
    fontSize: '11px',
    color: '#888',
    marginTop: '16px',
    lineHeight: '14px'
  }
};
